import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Tag, Smile, Frown, Meh, RefreshCw } from 'lucide-react'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import { listThreads, triageThread, suggestReply, type Thread } from '../api/client'
import { layout } from '../utils/layout'

const SENTIMENT_ICON = (s: number) => {
  if (s > 0.3) return <Smile className="w-4 h-4 text-green-400" />
  if (s < -0.3) return <Frown className="w-4 h-4 text-red-400" />
  return <Meh className="w-4 h-4 text-yellow-400" />
}

const CATEGORY_COLOR: Record<string, 'blue' | 'red' | 'yellow' | 'green' | 'slate'> = {
  question: 'blue',
  complaint: 'red',
  compliment: 'green',
  support: 'yellow',
}

export default function InboxView() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Thread | null>(null)
  const [statusFilter, setStatusFilter] = useState('open')

  const { data, isLoading } = useQuery({
    queryKey: ['threads', statusFilter],
    queryFn: () => listThreads(statusFilter, 20),
  })

  const triageMut = useMutation({
    mutationFn: (id: string) => triageThread(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  })

  const replyMut = useMutation({
    mutationFn: (id: string) => suggestReply(id),
  })

  return (
    <div>
      <PageHeader
        title="Unified Inbox"
        subtitle="Messages across all connected platforms · AI-powered triage"
        action={
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['threads'] })}
            className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Sync
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Filters */}
        <div className="flex gap-2">
          {['open', 'pending', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1 rounded-full capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-brand-700 text-brand-100'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex gap-4">
          {/* Thread list */}
          <div className="flex-1 min-w-0">
            <Card title={`${data?.total ?? 0} Threads`}>
              {isLoading ? (
                <div className="py-8 text-center text-slate-500 text-sm">Loading…</div>
              ) : !data?.threads.length ? (
                <EmptyState
                  icon={<MessageSquare className="w-12 h-12" />}
                  title="Inbox zero"
                  description="No threads match this filter. Connect a social account to start receiving messages."
                />
              ) : (
                <div className="divide-y divide-surface-border">
                  {data.threads.map(t => (
                    <div
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className={`py-3 px-2 cursor-pointer hover:bg-slate-800/40 rounded-lg transition-colors ${
                        selected?.id === t.id ? 'bg-slate-800/60' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {SENTIMENT_ICON(t.sentiment)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs text-slate-500 capitalize">{t.platform}</span>
                            <Badge color={CATEGORY_COLOR[t.category] ?? 'slate'}>{t.category}</Badge>
                          </div>
                          <p className="text-sm text-slate-300 truncate">{t.last_message}</p>
                          <p className="text-xs text-slate-600 mt-0.5">{new Date(t.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-80 shrink-0 space-y-3">
              <Card title="Thread Detail">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300 capitalize">{selected.platform}</span>
                    <Badge color={CATEGORY_COLOR[selected.category] ?? 'slate'}>{selected.category}</Badge>
                  </div>
                  <p className="text-sm text-slate-400">{selected.last_message}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => triageMut.mutate(selected.id)}
                      disabled={triageMut.isPending}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-xs py-1.5 rounded-lg transition-colors"
                    >
                      {triageMut.isPending ? 'Triaging…' : 'Auto-Triage'}
                    </button>
                    <button
                      onClick={() => replyMut.mutate(selected.id)}
                      disabled={replyMut.isPending}
                      className="flex-1 bg-brand-600 hover:bg-brand-700 text-white text-xs py-1.5 rounded-lg transition-colors"
                    >
                      {replyMut.isPending ? 'Thinking…' : 'Suggest Reply'}
                    </button>
                  </div>
                  {replyMut.data && (
                    <div className="space-y-2 pt-1">
                      <p className="text-xs text-slate-500">Suggested replies:</p>
                      {replyMut.data.suggestions.map((s, i) => (
                        <div key={i} className="bg-slate-800 rounded-lg p-2 text-xs text-slate-300">{s}</div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
